import React, { useEffect, useRef } from 'react';
import Typed from 'typed.js';
import SEO from "../../common/SEO";
import HeaderOne from '../../common/header/HeaderOne';
import HeaderTopBar from '../../common/header/HeaderTopBar';
import FooterTwo from '../../common/footer/FooterTwo';
import Copyright from '../../common/footer/Copyright';
import Separator from "../separator/Separator";
import SectionTitle from "../sectionTitle/SectionTitle";
import { 
    FiCheck, FiSmartphone, FiMessageCircle, FiShield, FiClock, FiArrowRight, 
    FiCpu, FiZap, FiGlobe, FiActivity, FiTarget, FiCode,
    FiAlertCircle, FiXCircle, FiFacebook, FiUser, FiMoon, FiAlertTriangle
} from "react-icons/fi";
import CountUp from 'react-countup';
import TrackVisibility from "react-on-screen";
import ScrollAnimation from "react-animate-on-scroll";
import { Link } from "react-router-dom";

const FeaturesData = [
    {
        icon: <FiCpu />,
        title: 'AI Replies',
        description: 'State of the art language model generates context aware, brand consistent responses at lightning speed.',
        number: '01'
    },
    {
        icon: <FiZap />,
        title: 'Human Like Delay',
        description: 'Replies post after a natural 30–60 second pause so your audience never suspects on reply automation.',
        number: '02'
    },
    {
        icon: <FiGlobe />,
        title: 'Duplicate Prevention',
        description: 'Proprietary logic ensures each comment receives exactly one reply no awkward double messages.',
        number: '03'
    },
    {
        icon: <FiActivity />,
        title: 'DB Analytics',
        description: 'Deep engagement data stored and surfaced via MongoDB track reply volume, performance, and trends.',
        number: '04'
    },
    {
        icon: <FiTarget />,
        title: 'Smart Spam Filtering',
        description: 'Intelligent algorithms prioritize important comments and filter out spam automatically with high accuracy.',
        number: '05'
    },
    {
        icon: <FiCode />,
        title: '24/7 Cloud Hosting',
        description: 'Enterprise grade cloud infrastructure guarantees maximum uptime with zero maintenance on your end.',
        number: '06'
    }
];

const CounterData = [
    {
        countNum : 100,
        countTitle: 'Engagement Boost',
        suffix: '%'
    },
    {
        countNum : 0,
        countTitle: 'Missed Comments',
        suffix: ''
    },
    {
        countNum : 45,
        countTitle: 'Avg. Response Time',
        suffix: 'ms'
    },
    {
        countNum : 24,
        countTitle: 'Hours Available',
        suffix: '/7'
    },
];

const ProcessData = [
    {
        step: 'Step 01',
        title: 'Connect Your Accounts',
        description: 'Securely link your social media profiles in minutes with our guided setup process.'
    },
    {
        step: 'Step 02',
        title: 'Train Your AI',
        description: 'Define your brand voice, response guidelines, and escalation rules for perfect interactions.'
    },
    {
        step: 'Step 03',
        title: 'Go Live & Relax',
        description: 'Activate AI auto reply and watch your engagement skyrocket while you focus on growth.'
    }
];

const ManualEngagementData = [
    {
        icon: <FiClock />,
        title: 'Hours Lost Every Day',
        description: 'Business owners and social media managers spend 2–4 hours daily just reading and replying to comments time that could drive real growth.'
    },
    {
        icon: <FiXCircle />,
        title: 'Slow Replies Kill Conversions',
        description: "Studies show 78% of customers buy from the brand that responds first. Delayed replies frustrate they send leads straight to competitors."
    },
    {
        icon: <FiAlertTriangle />,
        title: "You Can't Be Online 24/7",
        description: 'Comments flood in at midnight, on weekends, during holidays. Engagement opportunities vanish while your team sleeps.'
    }
];

const SolutionData = [
    {
        icon: <FiFacebook />,
        title: 'Instant replies on social media platforms',
        description: "Connected directly to Meta's official API for real time detection and response."
    },
    {
        icon: <FiUser />,
        title: 'AI that sounds genuinely human',
        description: 'Groq AI crafts contextual, brand aligned replies never robotic, always on point.'
    },
    {
        icon: <FiMoon />,
        title: 'Works perfectly while you sleep ',
        description: '24/7 cloud hosted automation means zero gaps in your engagement, ever again.'
    }
];

const AiCommentAutoreply = () => {
    const el = useRef(null);

    useEffect(() => {
        const typed = new Typed(el.current, {
            strings: ["Social Engagement Increase", "Customer Support Improvement", "Brand Presence Growth"],
            typeSpeed: 80,
            backSpeed: 60,
            backDelay: 2000,
            startDelay: 500,
            loop: true
        });

        return () => {
            typed.destroy();
        };
    }, []);

    return (
        <>
            <style>
                {`
                    .hero-title-container {
                        height: 300px;
                        margin-bottom: 5px;
                    }
                    @media (max-width: 768px) {
                        .hero-title-container {
                            height: 400px;
                        }
                    }
                    @media (max-width: 480px) {
                        .hero-title-container {
                            height: 450px;
                        }
                    }
                `}
            </style>
            <SEO 
                title="WhatsOn AI Reply - Auto Comment Responder"
                openGraph={{
                    title: "WhatsOn AI Reply Never Miss Comment Again",
                    description: "Automate your social media comment responses with WhatsOn AI Reply. Smart, fast, and brand-consistent replies 24/7.",
                    url: "/features-product",
                    type: "website"
                }}
            />
            <main className="page-wrapper theme-shape">
                <HeaderTopBar />
                <HeaderOne btnStyle="round btn-icon" HeaderSTyle="header-not-transparent" />

                {/* Hero Section - AI Vibe */}
                <div className="slider-area slider-style-1 bg-transparent variation-2" style={{ paddingTop: '0px', paddingBottom: '30px' }}>
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-lg-8 order-2 order-lg-1 mt_md--40 mt_sm--40">
                                <div className="inner text-start" style={{ paddingTop: '0px' }}>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true}>
                                        <div style={{ 
                                            display: 'inline-flex', 
                                            alignItems: 'center', 
                                            gap: '10px', 
                                            background: 'linear-gradient(135deg, rgba(5, 157, 255, 0.15), rgba(101, 73, 213, 0.15))', 
                                            padding: '8px 20px', 
                                            borderRadius: '50px', 
                                            marginBottom: '10px',
                                            marginTop: '20px',
                                            border: '1px solid rgba(5, 157, 255, 0.2)'
                                        }}>
                                            <span style={{ 
                                                width: '8px', 
                                                height: '8px', 
                                                borderRadius: '50%', 
                                                background: '#059DFF', 
                                                boxShadow: '0 0 10px #059DFF',
                                                animation: 'pulse 2s infinite'
                                            }}></span>
                                            <span style={{ fontSize: '14px', fontWeight: '600', background: 'linear-gradient(90deg, #059DFF, #6549D5)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                                                ✨ AI-Powered
                                            </span>
                                        </div>
                                    </ScrollAnimation>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={150}>
                                        <div className="hero-title-container">
                                            <h2 className="title theme-gradient display-two" style={{ marginBottom: '0', height: 'auto' }}>
                                                Transform Your {' '}
                                                <span ref={el} className="theme-gradient"></span>
                                            </h2>
                                        </div>
                                    </ScrollAnimation>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={300}>
                                        <p className="description" style={{ marginBottom: '', marginTop: '50px' }}>
                                            Meet WhatsOn AI Reply the intelligent comment automation system 24/7 with human like intelligence.
                                        </p>
                                    </ScrollAnimation>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={450}>
                                        <div className="button-group" aria-label="Get Contact">
                                            <Link className="btn-default btn-medium round btn-icon" to="/contact" style={{ 
                                                background: 'linear-gradient(135deg, #059DFF, #6549D5)', 
                                                border: 'none', 
                                                boxShadow: '0 10px 30px rgba(5, 157, 255, 0.3)'
                                            }}>
                                                Get Started Now
                                                <i className="icon">
                                                    <FiArrowRight />
                                                </i>
                                            </Link>
                                        </div>
                                    </ScrollAnimation>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={600}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '30px', marginTop: '10px', flexWrap: 'wrap' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <FiCheck style={{ color: '#059DFF' }} />
                                                <span style={{ color: '#acacac' }}>24/7 Support</span>
                                            </div>
                                        </div>
                                    </ScrollAnimation>
                                </div>
                            </div>
                            <div className="col-lg-4 order-1 order-lg-2">
                                <ScrollAnimation animateIn="fadeInRight" animateOnce={true} delay={200}>
                                    <div style={{ position: 'relative' }}>
                                        {/* Floating Elements */}
                                        <div style={{ 
                                            position: 'absolute', 
                                            top: '10%', 
                                            right: '5%', 
                                            background: 'rgba(101, 73, 213, 0.1)', 
                                            border: '1px solid rgba(101, 73, 213, 0.3)', 
                                            padding: '15px 20px', 
                                            borderRadius: '15px',
                                            animation: 'float 3s ease-in-out infinite'
                                        }}>
                                            <div style={{ fontSize: '24px', marginBottom: '5px' }}>⚡</div>
                                            <div style={{ fontSize: '12px', color: '#6549D5', fontWeight: '600' }}>Auto Reply</div>
                                        </div>
                                        <div style={{ 
                                            position: 'absolute', 
                                            bottom: '15%', 
                                            left: '5%', 
                                            background: 'rgba(5, 157, 255, 0.1)', 
                                            border: '1px solid rgba(5, 157, 255, 0.3)', 
                                            padding: '15px 20px', 
                                            borderRadius: '15px',
                                            animation: 'float 3s ease-in-out infinite 1s'
                                        }}>
                                            <div style={{ fontSize: '24px', marginBottom: '5px' }}>🤖</div>
                                            <div style={{ fontSize: '12px', color: '#059DFF', fontWeight: '600' }}>AI Active</div>
                                        </div>
                                        <div className="thumbnail">
                                            <img
                                                src="./images/bg/whatson-ai-reply.avif"
                                                alt="WhatsOn AI Reply - Auto Comment Responder"
                                                style={{ 
                                                    borderRadius: '30px', 
                                                    boxShadow: '0 30px 80px rgba(0,0,0,0.4), 0 0 60px rgba(5, 157, 255, 0.15)',
                                                    border: '1px solid rgba(255,255,255,0.05)'
                                                }}
                                            />
                                        </div>
                                    </div>
                                </ScrollAnimation>
                            </div>
                        </div>
                    </div>
                </div>

                <Separator />

                {/* The Cost of Manual Engagement Segment */}
                <div className="rn-section-gap">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-12">
                                <SectionTitle
                                    textAlign="text-center"
                                    radiusRounded=""
                                    subtitle="The Real Cost"
                                    title="The Cost of Manual Engagement"
                                    description="Every unanswered comment is a missed opportunity or worse, a customer walking out the door. Sound familiar?"
                                />
                            </div>
                        </div>
                        <div className="row mt--30">
                            {ManualEngagementData.map((item, index) => (
                                <div className="col-lg-4 col-md-6 col-12 mt--30" key={index}>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={index * 100}>
                                        <div style={{ 
                                            background: 'rgba(255, 255, 255, 0.03)', 
                                            padding: '40px 30px', 
                                            borderRadius: '20px', 
                                            height: '100%',
                                            border: '1px solid rgba(255, 255, 255, 0.05)',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-10px)';
                                            e.currentTarget.style.borderColor = 'rgba(255, 0, 0, 0.2)';
                                            e.currentTarget.style.background = 'rgba(255, 0, 0, 0.02)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.05)';
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                                        }}>
                                            <div style={{ 
                                                fontSize: '30px', 
                                                color: '#ff4b4b', 
                                                marginBottom: '20px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '60px',
                                                height: '60px',
                                                background: 'rgba(255, 75, 75, 0.1)',
                                                borderRadius: '12px'
                                            }}>
                                                {item.icon}
                                            </div>
                                            <h4 style={{ fontSize: '20px', marginBottom: '15px' }}>{item.title}</h4>
                                            <p style={{ color: '#acacac', marginBottom: '0', fontSize: '16px', lineHeight: '1.6' }}>{item.description}</p>
                                        </div>
                                    </ScrollAnimation>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <Separator />

                {/* The Solution Segment */}
                <div className="rn-section-gap">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-12">
                                <SectionTitle
                                    textAlign="text-center"
                                    radiusRounded=""
                                    subtitle="The Solution"
                                    title="Meet AutoReply AI"
                                    description="AutoReply AI is your always-on social media engagement engine <br> powered by cutting-edge AI to deliver human-like responses the moment a comment lands."
                                />
                            </div>
                        </div>
                        <div className="row mt--30">
                            {SolutionData.map((item, index) => (
                                <div className="col-lg-4 col-md-6 col-12 mt--30" key={index}>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={index * 100}>
                                        <div style={{ 
                                            background: 'linear-gradient(145deg, rgba(5, 157, 255, 0.05), rgba(101, 73, 213, 0.05))', 
                                            padding: '40px 30px', 
                                            borderRadius: '20px', 
                                            height: '100%',
                                            border: '1px solid rgba(5, 157, 255, 0.1)',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-10px)';
                                            e.currentTarget.style.borderColor = 'rgba(5, 157, 255, 0.3)';
                                            e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.3)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.borderColor = 'rgba(5, 157, 255, 0.1)';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}>
                                            <div style={{ 
                                                fontSize: '30px', 
                                                color: '#059DFF', 
                                                marginBottom: '20px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '60px',
                                                height: '60px',
                                                background: 'rgba(5, 157, 255, 0.1)',
                                                borderRadius: '12px'
                                            }}>
                                                {item.icon}
                                            </div>
                                            <h4 style={{ fontSize: '20px', marginBottom: '15px' }}>{item.title}</h4>
                                            <p style={{ color: '#acacac', marginBottom: '0', fontSize: '16px', lineHeight: '1.6' }}>{item.description}</p>
                                        </div>
                                    </ScrollAnimation>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <Separator />

                {/* How It Works Section */}
                <div className="rn-section-gap">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-12">
                                <SectionTitle
                                    textAlign="text-center"
                                    radiusRounded=""
                                    subtitle="Simple Process"
                                    title="How It Works"
                                    description="Get up and running in minutes with our streamlined three-step process."
                                />
                            </div>
                        </div>
                        <div className="row mt--50" style={{ justifyContent: 'center' }}>
                            {ProcessData.map((item, index) => (
                                <div className="col-lg-4 col-md-6 col-12" key={index} style={{ marginBottom: '30px' }}>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={index * 150}>
                                        <div style={{ 
                                            background: 'linear-gradient(135deg, rgba(15, 15, 17, 0.9), rgba(26, 26, 26, 0.9))', 
                                            padding: '40px 30px', 
                                            borderRadius: '20px', 
                                            height: '100%',
                                            border: '1px solid rgba(5, 157, 255, 0.1)',
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{ 
                                                position: 'absolute', 
                                                top: '-30px', 
                                                right: '-20px', 
                                                fontSize: '120px', 
                                                fontWeight: '900', 
                                                opacity: '0.03',
                                                background: 'linear-gradient(135deg, #059DFF, #6549D5)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent'
                                            }}>
                                                {index + 1}
                                            </div>
                                            <div style={{ 
                                                display: 'inline-block', 
                                                background: 'linear-gradient(135deg, rgba(5, 157, 255, 0.2), rgba(101, 73, 213, 0.2))', 
                                                padding: '8px 16px', 
                                                borderRadius: '10px', 
                                                fontSize: '13px', 
                                                fontWeight: '600',
                                                color: '#059DFF',
                                                marginBottom: '20px'
                                            }}>
                                                {item.step}
                                            </div>
                                            <h4 style={{ fontSize: '22px', marginBottom: '15px', color: '#fff' }}>{item.title}</h4>
                                            <p style={{ color: '#acacac', marginBottom: '0' }}>{item.description}</p>
                                        </div>
                                    </ScrollAnimation>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <Separator />

                {/* Features Section - Modern AI Style */}
                <div className="rn-service-area rn-section-gap">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-12">
                                <SectionTitle
                                    textAlign="text-center"
                                    radiusRounded=""
                                    subtitle="Powerful Capabilities"
                                    title="Everything You Need"
                                    description="Built with cutting-edge AI technology to deliver exceptional results."
                                />
                            </div>
                        </div>
                        <div className="row row--15 service-wrapper mt--50">
                            {FeaturesData.map((val, index) => (
                                <div className="col-lg-4 col-md-6 col-sm-6 col-12" key={index} style={{ marginBottom: '30px' }}>
                                    <ScrollAnimation
                                        animateIn="fadeInUp"
                                        animateOut="fadeInOut"
                                        animateOnce={true}
                                        delay={index * 100}
                                    >
                                        <div style={{ 
                                            background: 'linear-gradient(135deg, #0f0f11, #1a1a1a)', 
                                            padding: '40px 30px', 
                                            borderRadius: '20px', 
                                            height: '100%',
                                            border: '1px solid rgba(5, 157, 255, 0.08)',
                                            transition: 'all 0.3s ease',
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }} 
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-10px)';
                                            e.currentTarget.style.borderColor = 'rgba(5, 157, 255, 0.3)';
                                            e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.3), 0 0 40px rgba(5, 157, 255, 0.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.borderColor = 'rgba(5, 157, 255, 0.08)';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}>
                                            <div style={{ 
                                                position: 'absolute', 
                                                top: '20px', 
                                                right: '20px', 
                                                fontSize: '14px', 
                                                fontWeight: '700', 
                                                opacity: '0.2',
                                                color: '#fff'
                                            }}>
                                                {val.number}
                                            </div>
                                            <div style={{ 
                                                width: '60px', 
                                                height: '60px', 
                                                borderRadius: '15px', 
                                                background: 'linear-gradient(135deg, rgba(5, 157, 255, 0.2), rgba(101, 73, 213, 0.2))', 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center', 
                                                fontSize: '24px',
                                                color: '#059DFF',
                                                marginBottom: '25px'
                                            }}>
                                                {val.icon}
                                            </div>
                                            <div className="content">
                                                <h4 className="title w-600" style={{ marginBottom: '12px' }}>{val.title}</h4>
                                                <p className="description b1 color-gray mb--0">{val.description}</p>
                                            </div>
                                        </div>
                                    </ScrollAnimation>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <Separator />

                {/* Stats Section - Modern */}
                <div className="rwt-counterup-area rn-section-gap" style={{ 
                    background: 'linear-gradient(135deg, rgba(5, 157, 255, 0.05), rgba(101, 73, 213, 0.05))', 
                    borderRadius: '30px',
                    padding: '80px 20px',
                    margin: '0 20px'
                }}>
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-12">
                                <SectionTitle
                                    textAlign="text-center"
                                    radiusRounded=""
                                    subtitle="Proven Results"
                                    title="Real Impact, Real Fast"
                                    description="Numbers don't lie – see what our AI can do for your business."
                                />
                            </div>
                        </div>
                        <div className="row mt--50">
                            {CounterData.map((data, index) => (
                                <div className="col-lg-3 col-md-6 col-sm-6 col-12" key={index}>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={index * 100}>
                                        <div className="count-box counter-style-1 text-center" style={{ 
                                            background: 'rgba(15, 15, 17, 0.6)', 
                                            padding: '40px 20px',
                                            borderRadius: '20px',
                                            border: '1px solid rgba(5, 157, 255, 0.1)'
                                        }}>
                                            <TrackVisibility once>
                                                {({ isVisible }) => isVisible && 
                                                    <div className="count-number" style={{ 
                                                        fontSize: '48px', 
                                                        fontWeight: '800',
                                                        background: 'linear-gradient(135deg, #059DFF, #6549D5)',
                                                        WebkitBackgroundClip: 'text',
                                                        WebkitTextFillColor: 'transparent'
                                                    }}>
                                                        {isVisible ? (
                                                            <>
                                                                <CountUp end={data.countNum} />
                                                                <span style={{ fontSize: '32px' }}>{data.suffix}</span>
                                                            </>
                                                        ) : 0}
                                                    </div>
                                                }
                                            </TrackVisibility>
                                            <h5 className="title" style={{ marginTop: '10px', fontSize: '16px' }}>{data.countTitle}</h5>
                                        </div>
                                    </ScrollAnimation>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <Separator />

                {/* CTA Section - Modern AI Vibe */}
                <div className="rn-section-gap">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-12">
                                <ScrollAnimation animateIn="fadeInUp" animateOnce={true}>
                                    <div style={{ 
                                        background: 'linear-gradient(135deg, #059DFF 0%, #6549D5 50%, #E33FA1 100%)', 
                                        padding: '60px 40px',
                                        borderRadius: '30px',
                                        textAlign: 'center',
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}>
                                        <div style={{ 
                                            position: 'absolute', 
                                            top: '-50%', 
                                            left: '-50%', 
                                            width: '200%', 
                                            height: '200%', 
                                            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 60%)',
                                            animation: 'rotate 20s linear infinite'
                                        }}></div>
                                        <div style={{ position: 'relative', zIndex: '1' }}>
                                            <h2 style={{ fontSize: '36px', color: '#fff', marginBottom: '15px', fontWeight: '700' }}>
                                                Ready to Experience the Future?
                                            </h2>
                                            <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.9)', marginBottom: '30px' }}>
                                                Let's discuss how WhatsOn AI Reply can transform your social media engagement. 
                                                Pricing is customized based on your needs.
                                            </p>
                                            <div className="button-group" style={{ justifyContent: 'center' }}>
                                                <Link className="btn-default btn-medium round btn-icon" to="/contact" style={{ 
                                                    background: '#fff', 
                                                    color: '#059DFF',
                                                    border: 'none',
                                                    fontWeight: '600',
                                                    boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
                                                }}>
                                                    Contact Us for Pricing
                                                    <i className="icon">
                                                        <FiArrowRight />
                                                    </i>
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </ScrollAnimation>
                            </div>
                        </div>
                    </div>
                </div>

                <Separator />
               
                <FooterTwo />
                <Copyright />
            </main>
        </>
    )
}

export default AiCommentAutoreply;
